import { useEffect, useState } from 'react'
import { useAuthStore } from '../../stores/authStore'
import { useServerStore } from '@/shell/stores/serverStore'
import { useDmStore } from '@os/messenger/web/stores/dmStore'
import { userApi } from '../../services/api'

// Resolve a userId to a display name by walking the in-memory caches we
// already keep loaded for the chat surfaces — server members, DM
// members, and the current user. Falls back to `@user` so the chip
// still renders even if the cache is empty during a fresh page load.
function resolveDisplayName(userId: string): string | null {
  const me = useAuthStore.getState().user
  if (me?.id === userId) {
    return me.firstName ? `${me.firstName} ${me.lastName ?? ''}`.trim() : (me.username ?? null)
  }
  // Server members — every Server in the store carries a members[] used
  // by the rail member list, so this hits in O(servers × members) which
  // is fine for a couple thousand entries.
  const servers = useServerStore.getState().servers
  for (const s of servers) {
    // Orphaned members (user hard-deleted; FKs aren't enforced) carry a
    // null user — guard the predicate so the deref never throws.
    const m = s.members?.find((mem) => mem.user?.id === userId)
    if (m?.user) {
      const u = m.user
      const fn = (u as any).firstName as string | undefined
      const ln = (u as any).lastName as string | undefined
      return fn ? `${fn} ${ln ?? ''}`.trim() : (u.username ?? null)
    }
  }
  // DM members — group chats expose a member list with usernames;
  // 1:1 DMs have just two members so this is also cheap.
  const dms = useDmStore.getState().dmChannels
  for (const dm of dms) {
    const m = dm.members?.find((mem) => mem.user?.id === userId)
    if (m?.user) return m.user.username ?? null
  }
  return null
}

// Tiny inline chip rendered in place of `<@userId>` tokens inside chat
// bubbles. Click → open the user's public profile (same modal the
// server member list opens). Self-mentions get a blurple background to
// distinguish "you were tagged" from "someone else was tagged" at a
// glance.
//
// If the local cache has no entry for the userId (cross-server message
// from a user we never loaded, or a stale store right after login), we
// hit /api/users/:id once and memoize the result on the in-memory cache
// so subsequent renders don't refetch.
const inFlight = new Map<string, Promise<string | null>>()
const fetchedNames = new Map<string, string | null>()

async function fetchAndCacheName(userId: string): Promise<string | null> {
  if (fetchedNames.has(userId)) return fetchedNames.get(userId)!
  let promise = inFlight.get(userId)
  if (!promise) {
    promise = userApi
      .get(userId)
      .then((res) => {
        const u = res.data as any
        const name = u?.firstName ? `${u.firstName} ${u.lastName ?? ''}`.trim() : (u?.username ?? null)
        fetchedNames.set(userId, name)
        inFlight.delete(userId)
        return name
      })
      .catch(() => {
        fetchedNames.set(userId, null)
        inFlight.delete(userId)
        return null
      })
    inFlight.set(userId, promise)
  }
  return promise
}

export default function MentionChip({ userId }: { userId: string }) {
  const me = useAuthStore((s) => s.user)
  const isSelf = me?.id === userId
  const initial = resolveDisplayName(userId)
  const [name, setName] = useState<string | null>(initial)

  useEffect(() => {
    if (name) return
    let cancelled = false
    fetchAndCacheName(userId).then((n) => {
      if (!cancelled && n) setName(n)
    })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId])

  const display = name ?? '@user'
  return (
    <span
      data-mention-id={userId}
      className={
        isSelf
          ? 'inline-block px-1.5 rounded bg-discord-blurple/30 text-discord-blurple font-medium'
          : 'inline-block px-1.5 rounded bg-discord-blurple/15 text-discord-blurple font-medium'
      }
    >
      @{display}
    </span>
  )
}
